import * as React from "react";
import { applyTheme } from "@cloudscape-design/components/theming";
import { useDebug } from "@amzn/awsmp-max-agentic-renderer";
import { primaryButtonTheme } from "./primary-button-theme";
import { applyColorMode, resolveColorMode, subscribeToSystemScheme, systemPrefersDark } from "./theme/colorMode";
import { useThemePreferenceStore } from "./theme/themePreferenceStore";
import { DevThemeSwitcher } from "./theme/DevThemeSwitcher";

export interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Keeps one color mode on both design systems in the app — public Cloudscape and internal Polaris
 * (`@amzn/awsui-*`) — as the flag, the OS scheme and the dev switcher change, and themes public
 * Cloudscape primary buttons to match Polaris. The boot entry points apply the first mode.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { settings } = useDebug();
  const stored = useThemePreferenceStore((s) => s.preference);
  const prefersDark = React.useSyncExternalStore(subscribeToSystemScheme, systemPrefersDark);
  const { preference, isDark } = resolveColorMode({ stored, debugDarkMode: settings.darkMode, prefersDark });

  // Carries light and dark values, so it is applied once and follows applyMode
  React.useEffect(() => applyTheme({ theme: primaryButtonTheme }).reset, []);

  React.useLayoutEffect(() => applyColorMode(isDark), [isDark]);

  return (
    <div>
      {children}
      {import.meta.env.DEV && <DevThemeSwitcher selected={preference} />}
    </div>
  );
};
